import { fromJS, List, Map, Record } from 'immutable';

// CONSTANTS
import { ENTITY_IDS_BY_NAME } from 'constants/common';
import { DEFAULT_PER_PAGE, DEFAULT_OFFSET } from 'constants/table';
import { GENERIC_ERROR } from 'constants/errors.constants';

// SERVICES
import {
  ADD_FILTER,
  ARCHIVE_CUSTOMERS_BY_ID,
  CHANGE_PER_PAGE_VALUE,
  CLEAR_CUSTOMER,
  CLEAR_FILTERS,
  CLEAR_STRIPE_PORTAL_SESSION,
  CREATE_CUSTOMER_FAIL,
  CREATE_CUSTOMER_SUCCESS,
  GENERATE_STRIPE_PORTAL_SESSION_FAIL,
  GENERATE_STRIPE_PORTAL_SESSION_SUCCESS,
  GET_CUSTOMER_FAIL,
  GET_CUSTOMER_SUCCESS,
  GET_CUSTOMER_VEHICLES_FAIL,
  GET_CUSTOMER_VEHICLES_SUCCESS,
  GET_CUSTOMERS_FAIL,
  GET_CUSTOMERS_SUCCESS,
  GET_HISTORY_FAIL,
  GET_HISTORY_SUCCESS,
  POST_CUSTOMER_CHECKING_FAIL,
  POST_CUSTOMER_CHECKING_SUCCESS,
  REMOVE_FILTER,
  RESET_CUSTOMERS_DATA,
  SELECT_VIEW,
  SET_COLUMNS,
  SET_CUSTOMERS_OFFSET,
  SET_FILTERS,
  SET_SEARCH_QUERY,
  START_CUSTOMERS_REQUEST,
  START_LOADING,
  UPDATE_CUSTOMER_FAIL,
  UPDATE_CUSTOMER_SUCCESS,
} from './actions';
import {
  GET_USER_VIEWS_SUCCESS,
  CREATE_VIEW_SUCCESS,
  UPDATE_VIEW_SUCCESS,
  DELETE_VIEW_SUCCESS,
} from 'services/Views/actions';
import { withLatestRequest } from 'services/latestRequest';

const CustomerStateFactory = Record({
  views: undefined,
  activeView: undefined,
  filters: List(),
  columns: List(),

  customers: List(),
  searchedCustomers: List(),
  perPage: DEFAULT_PER_PAGE,
  offset: DEFAULT_OFFSET,
  total: 0,

  searchQuery: undefined,

  customer: Map(),

  vehicles: List(),
  history: List(),

  loading: false,
  errors: {},

  requestId: 0,

  stripePortalLink: '',
});

const initialState = CustomerStateFactory({});

function customersServiceReducer(state = initialState, action) {
  switch (action.type) {
    case START_LOADING:
      return state.set('loading', true);
    case START_CUSTOMERS_REQUEST:
      return state.set('loading', true);
    case SELECT_VIEW:
      return state
        .set('offset', DEFAULT_OFFSET)
        .set('activeView', action.payload.view)
        .set('filters', action.payload.view ? action.payload.view.get('rules') : List())
        .set('columns', action.payload.view ? action.payload.view.get('columns') : List());

    case ADD_FILTER:
      return state
        .update('filters', (filters) => filters.push(fromJS(action.payload.filter)))
        .set('customers', List())
        .set('searchedCustomers', List())
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case SET_FILTERS:
      return state.set('filters', action.payload.filters);
    case REMOVE_FILTER:
      return state
        .set('filters', state.get('filters').delete(action.payload.index))
        .set('customers', List())
        .set('searchedCustomers', List())
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case CREATE_CUSTOMER_SUCCESS:
      return state.set('loading', false);
    case CLEAR_FILTERS:
      return state
        .set('filters', state.get('activeView') ? state.get('activeView').get('rules') : fromJS([]))
        .set('customers', List())
        .set('searchedCustomers', List())
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case SET_COLUMNS:
      return state.set('columns', fromJS(action.payload.columns));
    case SET_SEARCH_QUERY:
      return state.set('offset', DEFAULT_OFFSET).set('searchQuery', fromJS(action.payload.searchQuery));
    case RESET_CUSTOMERS_DATA:
      return state.set('customers', List()).set('searchedCustomers', List());
    case GET_USER_VIEWS_SUCCESS: {
      const customerViews = fromJS(
        action.payload.views.results.filter((view) => view.type === ENTITY_IDS_BY_NAME.customers)
      );
      const defaultView = customerViews.find((view) => view.get('isDefault'));
      let activeView = state.get('activeView');
      let filters = state.get('filters');
      let columns = state.get('columns');

      if (!customerViews.includes(activeView)) {
        activeView = null;
      }

      if (typeof defaultView !== 'undefined') {
        activeView = defaultView;
        filters = defaultView.get('rules');
        columns = defaultView.get('columns');
      }

      return state
        .set('loading', false)
        .set('views', customerViews)
        .set('activeView', activeView)
        .set('filters', filters)
        .set('columns', columns);
    }
    case CREATE_VIEW_SUCCESS:
      if (action.payload.view.type === ENTITY_IDS_BY_NAME.customers) {
        return state
          .set('views', state.get('views').push(fromJS(action.payload.view)))
          .set('activeView', fromJS(action.payload.view));
      }

      return state;
    case UPDATE_VIEW_SUCCESS: {
      if (action.payload.view.type === ENTITY_IDS_BY_NAME.customers) {
        const index = state.get('views').findIndex((view) => view.get('id') === action.payload.view.id);
        const updatedView = fromJS(action.payload.view);

        return state
          .update('views', (views) => views.set(index, updatedView))
          .set('activeView', action.payload.resetDefault ? null : updatedView);
      }

      return state;
    }
    case DELETE_VIEW_SUCCESS: {
      const index = state.get('views').findIndex((view) => view.get('id') === action.payload.id);

      if (index > -1) {
        return state
          .set('views', state.get('views').remove(index))
          .set('activeView', null)
          .set('filters', List())
          .set('columns', List());
      }

      return state;
    }
    case SET_CUSTOMERS_OFFSET:
      return state.set('offset', action.payload.offset);
    case GET_CUSTOMERS_SUCCESS: {
      const pageNumber = action.payload.offset / state.get('perPage') + 1;
      const results = fromJS(action.payload.data.results);
      const newCustomers = state.get('customers').set(pageNumber, fromJS(action.payload.data.results));

      return state
        .set('customers', newCustomers)
        .set('searchedCustomers', results)
        .set('offset', action.payload.offset)
        .set('total', action.payload.data.count)
        .set('loading', false)
        .set('errors', fromJS({}));
    }
    case CREATE_CUSTOMER_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_CUSTOMERS_FAIL:
      return state
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR))
        .set('customers', List())
        .set('searchedCustomers', List())
        .set('perPage', DEFAULT_PER_PAGE)
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case CHANGE_PER_PAGE_VALUE:
      return state
        .set('customers', List())
        .set('searchedCustomers', List())
        .set('perPage', action.payload.perPage)
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case GET_CUSTOMER_SUCCESS:
      return state.set('customer', fromJS(action.payload.customer)).set('loading', false).set('errors', fromJS({}));
    case GET_CUSTOMER_FAIL:
      return state
        .set('customer', fromJS({}))
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case CLEAR_CUSTOMER:
      return state.set('customer', fromJS({})).set('history', List());
    case UPDATE_CUSTOMER_SUCCESS:
      return state.set('customer', fromJS(action.payload.customer)).set('loading', false).set('errors', fromJS({}));
    case UPDATE_CUSTOMER_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case ARCHIVE_CUSTOMERS_BY_ID: {
      return state.update('customers', (list) => {
        const pageNumber = action.payload.pageNumber;
        return fromJS(
          list
            .toJS()
            .map((customers: any[], page: number) =>
              page === pageNumber
                ? customers.filter((customer: any) => !action.payload.ids.includes(customer.id))
                : customers
            )
        );
      });
    }
    case GET_CUSTOMER_VEHICLES_SUCCESS:
      return state.set('vehicles', fromJS(action.payload.vehicles)).set('loading', false).set('errors', fromJS({}));
    case GET_CUSTOMER_VEHICLES_FAIL:
      return state
        .set('vehicles', List())
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_HISTORY_SUCCESS:
      return state.set('history', fromJS(action.payload.data)).set('errors', fromJS({}));
    case GET_HISTORY_FAIL:
      return state.set('history', List()).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GENERATE_STRIPE_PORTAL_SESSION_SUCCESS:
      if (action.payload.link) {
        return state.set('loading', false).set('stripePortalLink', action.payload.link).set('errors', fromJS({}));
      }
      return state.set('loading', false).set('errors', fromJS({}));
    case GENERATE_STRIPE_PORTAL_SESSION_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case CLEAR_STRIPE_PORTAL_SESSION:
      return state.set('stripePortalLink', '');
    case POST_CUSTOMER_CHECKING_SUCCESS:
      return state.set('loading', false).set('errors', fromJS({}));
    case POST_CUSTOMER_CHECKING_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    default:
      return state;
  }
}

export default withLatestRequest({
  start: START_CUSTOMERS_REQUEST,
  settle: [GET_CUSTOMERS_SUCCESS, GET_CUSTOMERS_FAIL],
  loadingKey: 'loading',
})(customersServiceReducer);
